/*
  Filtered collection (collection.filtered template, "enable_filters" on the Product Listing section).
  Self-contained: no dependency on component-collection-filters.js. Without JS the rail is a plain GET form.

  - Filter / sort / pill / pagination changes re-render the section through the Section Rendering API
    and swap the rail form, pills, Filters button label, drawer footer and results (grid + pagination).
  - URL is kept in sync with pushState; back/forward re-render the matching state.
  - Below 992px the same form is moved into the #FilterDrawer <dialog> while it is open.

  Hook for other scripts (quickview etc.): after every results swap this dispatches
    document 'collection:updated'  detail: { container: <section.collection-filtered>, url: <string> }
  The event does NOT fire on first page load, so bind initial cards yourself and re-bind on the event:
    function bindCards(root) { root.querySelectorAll('.card-product').forEach(...) }
    bindCards(document);
    document.addEventListener('collection:updated', (e) => bindCards(e.detail.container));
  Cards inside e.detail.container are brand-new nodes each time, so per-card listeners never double up.
*/
(function () {
  const section = document.querySelector('.collection-filtered[data-section-id]');
  if (!section || section.dataset.cfInit) return;
  section.dataset.cfInit = 'true';

  const sectionId = section.dataset.sectionId;
  const drawer = section.querySelector('[data-cf-drawer]');
  const drawerBody = section.querySelector('[data-cf-drawer-body]');
  const rail = section.querySelector('#FilterRail');
  const openBtn = section.querySelector('[data-cf-open]');
  const status = section.querySelector('[data-cf-status]');
  const desktop = window.matchMedia('(min-width: 992px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CHECK_DEBOUNCE = 300;
  const PRICE_DEBOUNCE = 600;

  let controller = null;
  let debounceTimer = null;
  let lastFocusKey = null;

  section.classList.add('cf-js');

  const getForm = () => section.querySelector('#FilterRailForm');
  const getResults = () => section.querySelector('[data-cf-results]');
  const getPills = () => section.querySelector('[data-cf-pills]');
  const isDrawerOpen = () => !!(drawer && drawer.open);

  /* ---------- URL building ---------- */

  // Keep ?view= (theme preview) on every URL the script builds or fetches.
  function withView(url) {
    const current = new URLSearchParams(window.location.search).get('view');
    if (current && !url.searchParams.has('view')) url.searchParams.set('view', current);
    return url;
  }

  function urlFromForm(form) {
    const params = new URLSearchParams();
    // The default sort adds nothing to the result, so it stays out of the URL.
    const sort = form.querySelector('select[name="sort_by"]');
    const defaultSort = sort ? sort.dataset.defaultSort : null;
    new FormData(form).forEach((value, key) => {
      if (value === '') return;
      if (key === 'sort_by' && value === defaultSort) return;
      params.append(key, value);
    });
    const url = new URL(window.location.pathname, window.location.origin);
    url.search = params.toString();
    return withView(url);
  }

  function urlFromHref(href) {
    return withView(new URL(href, window.location.origin));
  }

  /* ---------- Focus helpers ---------- */

  // Identify the control that triggered a change so it can be found again in the new markup.
  function focusKeyFor(el) {
    if (!el) return null;
    if (el.matches('input[type="checkbox"]')) return { name: el.name, value: el.value };
    if (el.id) return { id: el.id };
    return null;
  }

  function findByKey(key) {
    if (!key) return null;
    const form = getForm();
    if (!form) return null;
    if (key.id) return form.querySelector('#' + CSS.escape(key.id));
    return form.querySelector(
      'input[name="' + CSS.escape(key.name) + '"][value="' + CSS.escape(key.value) + '"]'
    );
  }

  function restoreFocus(key, focusWasInSwap) {
    if (!focusWasInSwap) return;
    const target = findByKey(key);
    if (target && !target.disabled) {
      target.focus({ preventScroll: true });
      return;
    }
    // Control is gone (e.g. a removed pill): move focus to the pills area, else the results.
    if (isDrawerOpen()) {
      const show = drawer.querySelector('[data-cf-show]');
      if (show) show.focus({ preventScroll: true });
      return;
    }
    const pills = getPills();
    const fallback = pills && pills.querySelector('.cf-pills') && pills.offsetParent ? pills : getResults();
    if (fallback) fallback.focus({ preventScroll: true });
  }

  /* ---------- Rendering ---------- */

  function setBusy(busy) {
    const results = getResults();
    if (results) results.setAttribute('aria-busy', busy ? 'true' : 'false');
    const show = drawer && drawer.querySelector('[data-cf-show]');
    if (show) show.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function resultsLabel(count) {
    return count + ' ' + (count === 1 ? 'product' : 'products');
  }

  // Remember which filter groups the shopper collapsed so a re-render keeps them collapsed.
  function groupStates(form) {
    const states = {};
    if (!form) return states;
    form.querySelectorAll('details.cf-group').forEach((group) => {
      const body = group.querySelector('.cf-group__body');
      if (body && body.id) states[body.id] = group.open;
    });
    return states;
  }

  function applyGroupStates(form, states) {
    form.querySelectorAll('details.cf-group').forEach((group) => {
      const body = group.querySelector('.cf-group__body');
      if (body && body.id in states) group.open = states[body.id];
    });
  }

  function swap(doc) {
    const oldForm = getForm();
    const newForm = doc.querySelector('#FilterRailForm');
    if (oldForm && newForm) {
      applyGroupStates(newForm, groupStates(oldForm));
      oldForm.replaceWith(newForm);
    }

    const pills = getPills();
    const newPills = doc.querySelector('[data-cf-pills]');
    if (pills && newPills) pills.innerHTML = newPills.innerHTML;

    const newOpenBtn = doc.querySelector('[data-cf-open]');
    if (openBtn && newOpenBtn) openBtn.textContent = newOpenBtn.textContent.trim();

    if (drawer) {
      const clear = drawer.querySelector('[data-cf-clear]');
      const newClear = doc.querySelector('[data-cf-clear]');
      if (clear && newClear) {
        clear.href = newClear.getAttribute('href');
        clear.classList.toggle('is-hidden', newClear.classList.contains('is-hidden'));
      }
      const show = drawer.querySelector('[data-cf-show]');
      const newShow = doc.querySelector('[data-cf-show]');
      if (show && newShow) show.textContent = newShow.textContent.trim();
    }

    const results = getResults();
    const newResults = doc.querySelector('[data-cf-results]');
    if (results && newResults) results.innerHTML = newResults.innerHTML;
  }

  function scrollToResults() {
    if (isDrawerOpen()) return;
    const results = getResults();
    if (!results) return;
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bs-navdrawer')) || 0;
    const top = results.getBoundingClientRect().top;
    if (top >= header) return;
    window.scrollTo({
      top: top + window.scrollY - header - 16,
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
  }

  async function render(url, options) {
    const opts = Object.assign({ push: true, focusKey: null, scroll: true }, options);
    clearTimeout(debounceTimer);
    if (controller) controller.abort();
    controller = new AbortController();
    const { signal } = controller;

    const fetchUrl = new URL(url.href);
    fetchUrl.searchParams.set('section_id', sectionId);

    setBusy(true);
    let html;
    try {
      const response = await fetch(fetchUrl.href, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Section render failed: ' + response.status);
      html = await response.text();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to a normal page load so the shopper still gets the result.
      window.location.assign(url.href);
      return;
    }
    if (signal.aborted) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.querySelector('[data-cf-results]')) {
      window.location.assign(url.href);
      return;
    }

    const active = document.activeElement;
    const focusWasInSwap =
      !active || active === document.body || !document.contains(active) ||
      !!(getForm() && getForm().contains(active)) ||
      !!(getPills() && getPills().contains(active)) ||
      !!(getResults() && getResults().contains(active));

    swap(doc);
    setBusy(false);
    controller = null;

    if (opts.push) history.pushState({ cf: true }, '', url.href);

    const form = getForm();
    const count = form ? parseInt(form.dataset.resultsCount, 10) : NaN;
    if (status && !isNaN(count)) status.textContent = resultsLabel(count);

    restoreFocus(opts.focusKey, focusWasInSwap);
    if (opts.scroll) scrollToResults();

    document.dispatchEvent(
      new CustomEvent('collection:updated', { detail: { container: section, url: url.href } })
    );
  }

  /* ---------- Events ---------- */

  section.addEventListener('change', (event) => {
    const form = getForm();
    const target = event.target;
    if (!form || !form.contains(target)) return;

    lastFocusKey = focusKeyFor(target);
    clearTimeout(debounceTimer);

    if (target.matches('select')) {
      render(urlFromForm(form), { focusKey: lastFocusKey });
      return;
    }
    const delay = target.matches('input[type="checkbox"]') ? CHECK_DEBOUNCE : PRICE_DEBOUNCE;
    debounceTimer = setTimeout(() => {
      // Re-read the form at fire time; it may have been replaced by an earlier response.
      const current = getForm();
      if (current) render(urlFromForm(current), { focusKey: lastFocusKey });
    }, delay);
  });

  section.addEventListener('submit', (event) => {
    if (event.target !== getForm()) return;
    event.preventDefault();
    render(urlFromForm(event.target), { focusKey: focusKeyFor(document.activeElement) });
  });

  section.addEventListener('click', (event) => {
    const link = event.target.closest('.cf-pill, .cf-pills__clear, [data-cf-clear], [data-pagelinks]');
    if (!link || !section.contains(link) || !link.href) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const isPage = link.matches('[data-pagelinks]');
    render(urlFromHref(link.href), {
      focusKey: link.matches('[data-cf-clear]') ? null : focusKeyFor(link),
      scroll: true
    }).then(() => {
      if (isPage) {
        const results = getResults();
        if (results) results.focus({ preventScroll: true });
      } else if (link.matches('[data-cf-clear]') && document.contains(link)) {
        link.focus({ preventScroll: true });
      }
    });
  });

  window.addEventListener('popstate', () => {
    render(new URL(window.location.href), { push: false, scroll: true });
  });

  history.replaceState({ cf: true }, '', window.location.href);

  /* ---------- Mobile drawer ---------- */

  // Scroll lock: overflow on <html> only. No position:fixed on body, so the page never jumps to top.
  let savedOverflow = '';
  let savedScrollY = 0;

  function lockScroll() {
    savedScrollY = window.scrollY;
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    document.documentElement.style.overflow = savedOverflow;
    if (Math.abs(window.scrollY - savedScrollY) > 1) window.scrollTo(0, savedScrollY);
  }

  function focusables(root) {
    return Array.from(
      root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.closest('.is-hidden') && el.getClientRects().length > 0);
  }

  function openDrawer() {
    if (!drawer || isDrawerOpen() || typeof drawer.showModal !== 'function') return;
    const form = getForm();
    if (form) drawerBody.appendChild(form);
    lockScroll();
    drawer.showModal();
    openBtn.setAttribute('aria-expanded', 'true');
    const close = drawer.querySelector('.cf-drawer__close');
    if (close) close.focus();
  }

  function closeDrawer() {
    if (isDrawerOpen()) drawer.close();
  }

  if (drawer && openBtn) {
    openBtn.addEventListener('click', openDrawer);

    drawer.addEventListener('click', (event) => {
      // The panel fills the dialog, so a click whose target is the dialog itself hit the backdrop.
      if (event.target === drawer || event.target.closest('[data-cf-close]')) closeDrawer();
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables(drawer);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Every close path (Escape, backdrop, close/Show buttons, resize) ends here.
    drawer.addEventListener('close', () => {
      const form = getForm();
      if (form && rail) rail.appendChild(form);
      unlockScroll();
      openBtn.setAttribute('aria-expanded', 'false');
      if (!desktop.matches) openBtn.focus({ preventScroll: true });
    });

    desktop.addEventListener('change', (event) => {
      if (event.matches) closeDrawer();
    });
  }

  /* ---------- Thumbnail hover (custom-js-collection.js) on swapped cards ---------- */

  document.addEventListener('collection:updated', () => {
    // customJsCollection is a top-level class in custom-js-collection.js; its constructor binds every .variant-thumb.
    if (typeof customJsCollection !== 'undefined') new customJsCollection();
  });
})();
